import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@apollo/client';
import { X, Upload as UploadIcon } from 'lucide-react';

const UPLOAD_DEAL_TEAM_DOCUMENT = gql`
  mutation UploadDealTeamDocument(
    $deal_team_id: ID!
    $title: String!
    $file: File!
    $shared_with_roles: [String!]
  ) {
    upload_deal_team_document(
      deal_team_id: $deal_team_id
      title: $title
      file: $file
      shared_with_roles: $shared_with_roles
    ) {
      vault_document {
        id
      }
      errors
    }
  }
`;

const ROLE_OPTIONS = [
  { value: 'cbv', label: 'Valuation Specialist (CBV)' },
  { value: 'business_broker', label: 'Business Broker' },
  { value: 'lawyer', label: 'Lawyer' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'lender', label: 'Lender' },
  { value: 'cepa_coach', label: 'CEPA Coach' },
];

const MAX_BYTES = 10 * 1024 * 1024; // 10MB — matches DocumentUploadModal.

const DealTeamUploadModal = ({ open, onClose, onSuccess, dealTeamId, dealTeamName }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [restrictByRole, setRestrictByRole] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [uploadDealTeamDocument] = useMutation(UPLOAD_DEAL_TEAM_DOCUMENT);

  // Reset internal state every time the modal closes — including after a
  // successful submit — so the next open is clean.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setTitle('');
      setRestrictByRole(false);
      setSelectedRoles([]);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setError(`${f.name} is larger than 10MB.`);
      return;
    }
    setError(null);
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  };

  const toggleRole = (role) => {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim() || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      // Empty `shared_with_roles` means "all team members can see".
      const rolesToShare = restrictByRole ? selectedRoles : [];

      const result = await uploadDealTeamDocument({
        variables: {
          deal_team_id: dealTeamId,
          title: title.trim(),
          file,
          shared_with_roles: rolesToShare,
        },
        context: { hasUpload: true },
      });

      const payload = result.data?.upload_deal_team_document;
      const errs = payload?.errors || [];
      if (errs.length > 0) throw new Error(errs.join(', '));
      if (!payload?.vault_document?.id) throw new Error('Upload did not return a document id.');

      if (window.ahoy) {
        window.ahoy.track('succeedo_deal_team_document_shared', {
          deal_team_id: dealTeamId,
          shared_with_roles: rolesToShare,
        });
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deal-team-upload-title"
    >
      <div className="w-full max-w-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
          <div>
            <h2 id="deal-team-upload-title" className="font-serif text-[20px] font-light text-[hsl(var(--foreground))]">
              Share a document
            </h2>
            <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">
              Upload to {dealTeamName || 'this deal team'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-[12px] font-mono uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">
              File
            </label>
            <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--succeedo-orange))]/60 cursor-pointer transition-colors">
              <UploadIcon className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <span className="text-[13px] text-[hsl(var(--foreground))] truncate">
                {file ? file.name : 'Choose a file (max 10MB)'}
              </span>
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                disabled={submitting}
              />
            </label>
          </div>

          <div>
            <label htmlFor="dealteam-doc-title" className="block text-[12px] font-mono uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">
              Title
            </label>
            <input
              id="dealteam-doc-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
              placeholder="e.g., LOI Draft v3"
              className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[14px] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/30"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={restrictByRole}
                onChange={(e) => setRestrictByRole(e.target.checked)}
                disabled={submitting}
                className="w-4 h-4 rounded border-[hsl(var(--border))] accent-[hsl(var(--succeedo-orange))]"
              />
              <span className="text-[13px] text-[hsl(var(--foreground))]">Restrict access by role</span>
            </label>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 ml-6">
              {restrictByRole
                ? 'Only checked roles will see this document.'
                : 'All team members will be able to view and download.'}
            </p>

            {restrictByRole && (
              <div className="mt-3 ml-6 grid grid-cols-1 gap-1.5">
                {ROLE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(opt.value)}
                      onChange={() => toggleRole(opt.value)}
                      disabled={submitting}
                      className="w-4 h-4 rounded border-[hsl(var(--border))] accent-[hsl(var(--succeedo-orange))]"
                    />
                    <span className="text-[13px] text-[hsl(var(--foreground))]">{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!file || !title.trim() || submitting || (restrictByRole && selectedRoles.length === 0)}
              className="px-4 py-2 rounded-full text-[13px] font-medium bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {submitting ? 'Uploading…' : 'Share Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

DealTeamUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  dealTeamId: PropTypes.string.isRequired,
  dealTeamName: PropTypes.string,
};

export default DealTeamUploadModal;
